import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Stack } from "@mantine/core";
import { Company } from "@/types";
import {
  isValidDuns,
  isValidNorwegianOrgNumber,
  NORWEGIAN_ORG_NUMBER_REGEX,
  normalizeOrgNumber,
} from "@/utils/orgNumber";
import Modal, { ModalContent, ModalCTAs } from "@/components/molecules/Modal";
import TextInput from "@/components/atoms/TextInput";
import Text from "@/components/atoms/Text";
import CTA from "@/components/atoms/CTA";

export interface ApproveCompanyValues {
  orgNumber: string;
  duns: string;
}

interface ApproveCompanyModalProps {
  company: Company;
  opened: boolean;
  loading: boolean;
  onClose: () => void;
  onApprove: (values: ApproveCompanyValues) => void;
}

export const isNorwegianCompany = (company: Company) =>
  company.companyCountryCode?.iso3?.toUpperCase() === "NOR" || company.companyCountryCode?.iso2?.toUpperCase() === "NO";

export const getOrgNumberErrorKey = (orgNumber: string, isNorwegian: boolean) => {
  const normalized = normalizeOrgNumber(orgNumber);
  if (!normalized) return "validation:orgNumberRequired";
  if (!isNorwegian) return undefined;
  if (!NORWEGIAN_ORG_NUMBER_REGEX.test(normalized)) return "validation:norwegianOrgNumberLength";
  if (!isValidNorwegianOrgNumber(normalized)) return "validation:invalidNorwegianOrgNumber";
  return undefined;
};

export const getDunsErrorKey = (duns: string) => {
  if (!duns.trim()) return "validation:dunsNumberRequired";
  if (!isValidDuns(duns)) return "validation:dunsLength";
  return undefined;
};

export default function ApproveCompanyModal({
  company,
  opened,
  loading,
  onClose,
  onApprove,
}: ApproveCompanyModalProps) {
  const { t } = useTranslation();
  const isNorwegian = isNorwegianCompany(company);

  const [orgNumber, setOrgNumber] = useState("");
  const [duns, setDuns] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!opened) return;
    setOrgNumber(normalizeOrgNumber(company.vatNumber));
    setDuns(company.duns != null ? String(company.duns) : "");
    setTouched(false);
  }, [opened, company.vatNumber, company.duns]);

  const orgNumberErrorKey = getOrgNumberErrorKey(orgNumber, isNorwegian);
  const dunsErrorKey = getDunsErrorKey(duns);
  const isValid = !orgNumberErrorKey && !dunsErrorKey;

  const handleApprove = () => {
    setTouched(true);
    if (!isValid) return;
    onApprove({ orgNumber: normalizeOrgNumber(orgNumber), duns: duns.trim() });
  };

  return createPortal(
    <Modal opened={opened} onClose={onClose} title={t("commonAdmin:approveCompany.title")}>
      <ModalContent>
        <Stack gap="md" p="20px 23px">
          <Text size="sm">
            {isNorwegian
              ? t("commonAdmin:approveCompany.descriptionNorwegian")
              : t("commonAdmin:approveCompany.descriptionForeign")}
          </Text>
          <TextInput
            id="approve-org-number"
            data-testid="approve-org-number-input"
            label={t("settings:partnersSettings.organisationNumber")}
            value={orgNumber}
            onChange={(e) => setOrgNumber(e.currentTarget.value)}
            onBlur={() => setTouched(true)}
            error={touched && orgNumberErrorKey ? t(orgNumberErrorKey) : undefined}
          />
          <TextInput
            id="approve-duns"
            data-testid="approve-duns-input"
            label={t("commonAdmin:approveCompany.duns")}
            value={duns}
            onChange={(e) => setDuns(e.currentTarget.value)}
            onBlur={() => setTouched(true)}
            error={touched && dunsErrorKey ? t(dunsErrorKey) : undefined}
          />
        </Stack>
      </ModalContent>
      <ModalCTAs>
        <CTA intent="secondary" onClick={onClose} disabled={loading}>
          {t("actions:cancel")}
        </CTA>
        <CTA onClick={handleApprove} loading={loading} disabled={touched && !isValid} data-testid="approve-submit-btn">
          {t("actions:approve")}
        </CTA>
      </ModalCTAs>
    </Modal>,
    document.body
  );
}
